import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { RefSelectProps } from 'antd/lib/select';
import { WithLogic, withLogicContext } from 'App';
import { LatLng } from 'common/model/geo';
import { getParents } from 'common/utils';
import { debounce } from 'debounce';
import { Suggestions } from 'logic/routing/logic/suggestion';
import * as React from 'react';
import { Subject, Subscription } from 'rxjs';
import PlacesAutocomplete from './components/PlacesAutocomplete';

export type RouteSelectData =
    | {
          type: 'LAT_LNG';
          value: LatLng;
      }
    | {
          type: 'GOOGLE_GEOCODER';
          value: Suggestions['googleGeocoder'][0];
      }
    | {
          type: 'GOOGLE';
          value: Suggestions['google'][0];
      }
    | {
          type: 'EUROWAG';
          value: Suggestions['eurowag'][0];
      };

export type RouteSelect = (data?: RouteSelectData) => void;

interface State {
    suggestions: Suggestions;
    searchText: string;
    opened: boolean;
}

interface Props extends WithLogic {
    autofocus?: boolean;
    innerref?: React.RefObject<RefSelectProps>;
    updateSearchValue?: Subject<string>;
    size?: SizeType;
    className?: string;
    loading?: boolean;
    tabMenuClassName?: string;
    placeHolder?: React.ReactNode;
    dropdownMatchSelectWidth?: number;
    dropdownAlign?: any;
    allowClear?: boolean;
    open?: boolean;
    onSelect?: RouteSelect;
    onEscape?: () => void;
    onChange?: (text: string) => void;
    onBlur?: () => void;
}

class PlacesAutocompleteModule extends React.Component<Props, State> {
    private _suggestionSubscription?: Subscription;

    constructor(props: Props) {
        super(props);
        this.state = {
            searchText: '',
            suggestions: {
                eurowag: [],
                google: [],
                latLng: undefined,
                googleGeocoder: []
            },
            opened: false
        };
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
        this._suggestionSubscription = this.props.logic
            .routing()
            .suggestion()
            .suggestionsData.subscribe(suggestions => {
                this.setState({
                    suggestions
                });
            });
    }

    componentWillUnmount() {
        document.addEventListener('mousedown', this.handleClickOutside);
        this._suggestionSubscription?.unsubscribe();
    }

    handleClickOutside = (event: MouseEvent): void => {
        const containers = ['.places-autocomplete-dropdown'];
        let isContainer = false;
        for (let i = 0; i < containers.length; i++) {
            if (getParents(event.target, containers[i])) {
                isContainer = true;
                break;
            }
        }

        if (!isContainer && (event.target as Element).classList.contains('ant-select-selection-search-input')) {
            isContainer = true;
        }

        if (!isContainer && (event.target as Element).classList.contains('ant-tabs-dropdown-menu-item')) {
            isContainer = true;
        }

        if (!isContainer) {
            this.setState({
                opened: false
            });
        }
    };

    handleOnClick = (e: React.MouseEvent<Element>) => {
        if ((e.currentTarget as Element).classList.contains('ant-select-show-search')) {
            this.setState({
                opened: true
            });
        }
    };

    handleOnTabClick = () => {
        this.setState({
            opened: true
        });
        this.props.innerref?.current?.focus();
    };

    render() {
        return (
            <PlacesAutocomplete
                className={this.props.className}
                placeHolder={this.props.placeHolder}
                tabMenuClassName={this.props.tabMenuClassName}
                autoFocus={this.props.autofocus}
                innerref={this.props.innerref}
                loading={this.props.loading}
                size={this.props.size}
                text={this.state.searchText}
                open={this.state.opened}
                suggestions={this.state.suggestions}
                updateSearchValue={this.props.updateSearchValue}
                dropdownMatchSelectWidth={this.props.dropdownMatchSelectWidth}
                dropdownAlign={this.props.dropdownAlign}
                onChange={this._onSearchChange}
                onChangeSearch={(text: string) => {
                    this.setState({
                        opened: text !== ''
                    });
                }}
                onClick={this.handleOnClick}
                onSelect={this._onSelect}
                onEscape={this._onEscape}
                onBlur={this.props.onBlur}
                onTabClick={this.handleOnTabClick}
                allowClear={this.props.allowClear}
            />
        );
    }

    private _onEscape = () => {
        this.setState({
            opened: false
        });
        this.props.onEscape?.();
    };

    private _onSelect = (id?: string | null) => {
        const latLngSuggestion = this.state.suggestions.latLng;
        if (id === 'LAT_LNG' && latLngSuggestion) {
            this.props.onSelect?.({
                type: 'LAT_LNG',
                value: latLngSuggestion
            });
        }

        const googleGeocoderSuggestion = this.state.suggestions.googleGeocoder.find(s => s.place_id === id);
        if (googleGeocoderSuggestion) {
            this.props.onSelect?.({
                type: 'GOOGLE_GEOCODER',
                value: googleGeocoderSuggestion
            });
        }

        const googleSuggestion = this.state.suggestions.google.find(s => s.place_id === id);
        if (googleSuggestion) {
            this.props.onSelect?.({
                type: 'GOOGLE',
                value: googleSuggestion
            });
        }
        const eurowagSuggestion = this.state.suggestions.eurowag.find(s => s.id === id);
        if (eurowagSuggestion) {
            this.props.onSelect?.({
                type: 'EUROWAG',
                value: eurowagSuggestion
            });
        }

        if (eurowagSuggestion || googleSuggestion || googleGeocoderSuggestion || latLngSuggestion) {
            this.setState({
                searchText: '',
                suggestions: {
                    eurowag: [],
                    google: [],
                    googleGeocoder: [],
                    latLng: undefined
                }
            });
        }

        if (!eurowagSuggestion && !googleSuggestion && !googleGeocoderSuggestion && !latLngSuggestion) {
            this.props.onSelect?.(undefined);
        }
    };

    private _onSearchChange = debounce(async (text: string) => {
        this.props.onChange?.(text);
        if (text === '') {
            this.setState({
                suggestions: {
                    eurowag: [],
                    google: [],
                    googleGeocoder: [],
                    latLng: undefined
                },
                opened: false
            });
        } else {
            this.setState({
                opened: true
            });
            this.props.logic.routing().suggestion().getSuggestions(text);
        }
    }, 5e2);
}

export default withLogicContext(PlacesAutocompleteModule);
